import { useEffect } from "react";
import { useSelector } from "react-redux";
// eslint-disable-next-line import/no-unresolved -- CRA's import resolver doesn't read the package's `exports` map
import { setEntry, setProperty, setOrg, setTags } from "@hyly/observability/react";

import {
  selectPropertyId,
  getEntryId,
  getOrgId,
  selectSiteId,
  selectPropertyName,
  selectIsJAI,
  getSpanishLanguage,
  getIsParentView,
  getStandalonePage,
} from "slices";

/**
 * Syncs the chatbot's anonymous/lead identity into observability (Sentry).
 *
 * The chatbot has no login, so it identifies sessions by:
 *  - `property` (the property_id the widget is embedded for),
 *  - `entry` (the CRM lead id, known only after a booking / contact create),
 *  - `org` (the tenant, resolved once mascot-management data lands), and
 *  - a set of coarse, non-PII flow tags (mode / jai / page / locale / site /
 *    property_name) so issues can be filtered by tenant, property, and flow.
 *
 * We watch the Redux selectors rather than calling the package from inside the
 * reducers (property-slice etc.) — reducers must stay pure, and watching the
 * selectors keeps identity reactive as late-arriving values (org, name, locale)
 * land after the property/mascot fetches.
 *
 * Secrets/PII are NEVER tagged (api_key, mlApiKey, appSSID, email, phone, chat
 * text). High-cardinality ids (conversationID) stay in the Redux state context,
 * not as tags.
 *
 * Renders nothing. Mounted in `src/index.js` inside <Provider> ONLY when
 * observability is enabled (production), so non-prod never runs this.
 */
export default function ObservabilityIdentity() {
  const propertyId = useSelector(selectPropertyId);
  const entryId = useSelector(getEntryId);
  const orgId = useSelector(getOrgId);
  const siteId = useSelector(selectSiteId);
  const propertyName = useSelector(selectPropertyName);
  const isJAI = useSelector(selectIsJAI);
  const isSpanishLanguage = useSelector(getSpanishLanguage);
  const isParentView = useSelector(getIsParentView);
  const standalonePage = useSelector(getStandalonePage);

  useEffect(() => {
    if (propertyId) {
      setProperty({ id: String(propertyId) });
    }
  }, [propertyId]);

  useEffect(() => {
    // setEntry → Sentry user.id + tag `entry` + tag `user.role="lead"`,
    // so "users affected" counts leads.
    if (entryId) {
      setEntry({ id: String(entryId) });
    }
  }, [entryId]);

  useEffect(() => {
    // org_id is absent from initialState — set the `org` tag only once it lands.
    if (orgId) {
      setOrg({ id: String(orgId) });
    }
  }, [orgId]);

  useEffect(() => {
    // Coarse, filterable, non-PII context tags. Include only defined values so
    // we never write empty/undefined tags into Sentry's index.
    const tags = {
      mode: isParentView ? "standalone" : "embedded",
      jai: isJAI ? "true" : "false",
      locale: isSpanishLanguage ? "es" : "en",
    };

    if (propertyName) {
      tags.property_name = String(propertyName);
    }

    if (siteId) {
      tags.site = String(siteId);
    }

    if (standalonePage) {
      tags.page = String(standalonePage);
    }

    setTags(tags);
  }, [isParentView, isJAI, isSpanishLanguage, propertyName, siteId, standalonePage]);

  return null;
}
